import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Braces, LockKeyhole, Mail, MailCheck, Save, UserRound } from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";

import {
  Account,
  ProfileDocument,
  ProfileField,
  ProfileNamespace,
  ProfileNamespaceDetail,
  api,
  formatDate,
} from "../api";
import { EmptyState, ErrorState, LoadingState, MutationMessage, PageHeader, Section, StatusBadge } from "./components";
import { localeOptions, timeZoneOptions } from "../locale-options";

export function ProfilePage({ account }: { account: Account }) {
  const queryClient = useQueryClient();
  const [core, setCore] = useState({
    displayName: account.displayName,
    locale: account.locale,
    timeZone: account.timeZone,
  });
  useEffect(() => {
    setCore({ displayName: account.displayName, locale: account.locale, timeZone: account.timeZone });
  }, [account]);
  const update = useMutation({
    mutationFn: () => api<Account>("/api/v1/account", {
      method: "PATCH",
      body: JSON.stringify({ expectedVersion: account.version, ...core }),
    }),
    onSuccess: (value) => queryClient.setQueryData(["account"], value),
  });
  const resend = useMutation({
    mutationFn: () => api<{ status: string }>("/api/v1/registrations/email-verification/requests", {
      method: "POST",
      body: JSON.stringify({ email: account.email.address }),
    }),
  });

  return (
    <>
      <PageHeader eyebrow="IDENTITY" title="个人资料" description="管理基础身份和应用可用的动态资料。" />
      <Section title="基础身份" description={`账号建立于 ${formatDate(account.createdAt)}`}>
        <div className="profile-identity-summary">
          <span className="profile-avatar">{account.displayName.slice(0, 1).toUpperCase()}</span>
          <div><strong>{account.displayName}</strong><span>@{account.username}</span><small>{account.email.address}</small></div>
          <StatusBadge tone={account.email.verified ? "good" : "warning"}>{account.email.verified ? <><MailCheck />邮箱已验证</> : <><Mail />邮箱待验证</>}</StatusBadge>
          {!account.email.verified && <button className="secondary-button" disabled={resend.isPending || resend.isSuccess} type="button" onClick={() => resend.mutate()}><Mail />{resend.isSuccess ? "验证邮件已发送" : "重新发送验证邮件"}</button>}
        </div>
        <form className="settings-form profile-core-form" onSubmit={(event) => { event.preventDefault(); update.mutate(); }}>
          <div className="field-grid three">
            <label><span>显示名称</span><input required maxLength={80} autoComplete="name" value={core.displayName} onChange={(event) => setCore({ ...core, displayName: event.target.value })} /></label>
            <label><span>语言</span><select required value={core.locale} onChange={(event) => setCore({ ...core, locale: event.target.value })}>{localeOptions(core.locale).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
            <label><span>时区</span><select required value={core.timeZone} onChange={(event) => setCore({ ...core, timeZone: event.target.value })}>{timeZoneOptions(core.timeZone).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
          </div>
          <MutationMessage error={update.error ?? resend.error} success={update.isSuccess && "基础资料已更新"} />
          <div className="form-actions"><button className="primary-button" disabled={update.isPending} type="submit"><Save />保存修改</button></div>
        </form>
      </Section>
      <DynamicProfiles />
    </>
  );
}

function DynamicProfiles() {
  const namespaces = useQuery({
    queryKey: ["profile-namespaces"],
    queryFn: () => api<{ namespaces: ProfileNamespace[] }>("/api/v1/profile/namespaces"),
  });
  const [selected, setSelected] = useState<string | null>(null);
  useEffect(() => {
    if (!selected && namespaces.data?.namespaces[0]) setSelected(namespaces.data.namespaces[0].publicId);
  }, [namespaces.data, selected]);

  return (
    <Section title="扩展资料" description="字段由平台或组织动态配置；敏感值独立加密保存。">
      {namespaces.isPending && <LoadingState />}
      {namespaces.error && <ErrorState error={namespaces.error} retry={() => void namespaces.refetch()} />}
      {namespaces.data?.namespaces.length === 0 && <EmptyState icon={Braces} title="暂无扩展资料" detail="可用的资料命名空间会显示在这里。" />}
      {(namespaces.data?.namespaces.length ?? 0) > 0 && (
        <div className="profile-workspace">
          <div className="profile-tabs" role="tablist" aria-label="资料命名空间">
            {namespaces.data?.namespaces.map((namespace) => (
              <button role="tab" aria-selected={selected === namespace.publicId} className={selected === namespace.publicId ? "active" : ""} key={namespace.publicId} type="button" onClick={() => setSelected(namespace.publicId)}>
                <UserRound /><span>{namespace.displayName}</span><small>{namespace.ownerType}</small>
              </button>
            ))}
          </div>
          {selected && <ProfileDocumentEditor namespaceId={selected} />}
        </div>
      )}
    </Section>
  );
}

function ProfileDocumentEditor({ namespaceId }: { namespaceId: string }) {
  const client = useQueryClient();
  const detail = useQuery({
    queryKey: ["profile-namespace", namespaceId],
    queryFn: () => api<ProfileNamespaceDetail>(`/api/v1/profile/namespaces/${namespaceId}`),
  });
  const document = useQuery({
    queryKey: ["profile-document", namespaceId],
    queryFn: () => api<ProfileDocument>(`/api/v1/profile/namespaces/${namespaceId}/document`),
  });
  const initial = useMemo(() => buildValues(detail.data?.fields ?? [], document.data), [detail.data, document.data]);
  const [values, setValues] = useState<Record<string, string | boolean>>(initial);
  useEffect(() => setValues(initial), [initial]);
  const save = useMutation({
    mutationFn: () => api<ProfileDocument>(`/api/v1/profile/namespaces/${namespaceId}/document`, {
      method: "PATCH",
      body: JSON.stringify({
        expectedVersion: document.data?.version ?? 0,
        values: Object.fromEntries((detail.data?.fields ?? []).filter((field) => field.subjectWritable).map((field) => [field.key, encodedValue(field, values[field.key])])),
      }),
    }),
    onSuccess: (next) => client.setQueryData(["profile-document", namespaceId], next),
  });

  if (detail.isPending || document.isPending) return <LoadingState />;
  if (detail.error || document.error || !detail.data || !document.data) return <ErrorState error={detail.error ?? document.error} />;
  const fields = detail.data.fields.filter((field) => field.status === "ACTIVE");
  if (fields.length === 0) return <EmptyState icon={Braces} title="暂无字段" detail="这个命名空间尚未定义资料字段。" />;

  return (
    <form className="dynamic-profile-form" onSubmit={(event) => { event.preventDefault(); save.mutate(); }}>
      <div className="dynamic-profile-heading"><div><strong>{detail.data.namespace.displayName}</strong><span>{detail.data.namespace.description ?? detail.data.namespace.key}</span></div><StatusBadge>{fields.length} 个字段</StatusBadge></div>
      <div className="field-grid">
        {fields.map((field) => (
          <ProfileInput key={field.publicId} field={field} value={values[field.key]} onChange={(value) => setValues({ ...values, [field.key]: value })} />
        ))}
      </div>
      <MutationMessage error={save.error} success={save.isSuccess && "扩展资料已更新"} />
      {fields.some((field) => field.subjectWritable) && <div className="form-actions"><button className="primary-button" type="submit" disabled={save.isPending}><Save />保存资料</button></div>}
    </form>
  );
}

function ProfileInput({ field, value, onChange }: { field: ProfileField; value: string | boolean | undefined; onChange: (value: string | boolean) => void }) {
  const sensitive = field.sensitivity === "SENSITIVE";
  if (field.valueType === "BOOLEAN") {
    return <label className="toggle-field"><span><strong>{field.displayName}</strong><small>{field.description}</small></span><input type="checkbox" disabled={!field.subjectWritable} checked={value === true} onChange={(event) => onChange(event.target.checked)} /></label>;
  }
  const type = field.valueType === "DATE" ? "date" : field.valueType === "DATETIME" ? "datetime-local" : field.valueType === "EMAIL" ? "email" : field.valueType === "URI" ? "url" : field.valueType === "INTEGER" || field.valueType === "DECIMAL" ? "number" : "text";
  return (
    <label>
      <span>{field.displayName}{sensitive && <LockKeyhole aria-label="敏感字段" />}</span>
      <input disabled={!field.subjectWritable} type={type} step={field.valueType === "DECIMAL" ? "any" : undefined} value={typeof value === "string" ? value : ""} placeholder={sensitive ? "留空则保持原值" : undefined} onChange={(event) => onChange(event.target.value)} />
      {field.description && <small>{field.description}</small>}
    </label>
  );
}

function buildValues(fields: ProfileField[], document?: ProfileDocument): Record<string, string | boolean> {
  return Object.fromEntries(fields.map((field) => {
    const raw = field.sensitivity === "SENSITIVE" ? document?.sensitiveValues[field.key]?.value : document?.values[field.key];
    if (field.valueType === "BOOLEAN") return [field.key, raw === true];
    if (field.valueType === "TEXT_LIST" && Array.isArray(raw)) return [field.key, raw.join(", ")];
    if (field.valueType === "DATETIME" && typeof raw === "string") return [field.key, localDateTime(raw)];
    return [field.key, raw == null ? "" : String(raw)];
  }));
}

function localDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const part = (number: number) => String(number).padStart(2, "0");
  return `${date.getFullYear()}-${part(date.getMonth() + 1)}-${part(date.getDate())}T${part(date.getHours())}:${part(date.getMinutes())}`;
}

function encodedValue(field: ProfileField, value: string | boolean | undefined): unknown {
  if (typeof value === "boolean") return value;
  if (!value) return field.sensitivity === "SENSITIVE" ? undefined : null;
  if (field.valueType === "INTEGER") return Number.parseInt(value, 10);
  if (field.valueType === "DECIMAL") return Number(value);
  if (field.valueType === "DATETIME") return new Date(value).toISOString();
  if (field.valueType === "TEXT_LIST") return value.split(",").map((item) => item.trim()).filter(Boolean);
  return value;
}
